import { PortalAccountDomain } from '../../utils/enums'
import { Answer, ApplicationStatus, Question, SelectOption } from '../../utils/types'

export type OptionWithLabel = SelectOption<string> & { label: string }

export class IBAnswers {
    private question: Question
    private _answers: Answer[] = []
    constructor(question: Question) {
        this.question = question
    }
    public addAnswer(label: string, meta: string = '') {
        const a = this.question.answers.find(a => a.label === label)
        if (a) this._answers.push({ ...a, other: meta })
    }
    public get fullAnswer() {
        return { ...this.question, answers: this._answers }
    }
}

export type ApplicationAccountType = 'CORPORATE' | 'INDIVIDUAL'

export type ContactType = 'AUTHORIZED_REPRESENTATIVE' | 'DIRECTOR' | 'SHAREHOLDER'

export type PartnershipType = 'AFFILIATES' | 'IB'

export interface PerformanceReport {
    activeTraders: number
    registrations: number
    approvedApplications: number
    commissions: number
    notionalValue: number
    netFunding: number
    deposits: number
    withdrawals: number
    conversionRate: number
    activeTradersPercentage: number
    registrationsPercentage: number
    approvedApplicationsPercentage: number
    commissionsPercentage: number
    notionalValuePercentage: number
    netFundingPercentage: number
    depositsPercentage: number
    withdrawalsPercentage: number
    conversionRatePercentage: number
}

export interface GetPerformanceSummaryParams {
    partnerId: number
    fromCurrentPeriod?: string
    toCurrentPeriod?: string
    fromPreviousPeriod?: string
    toPreviousPeriod?: string
}


export type DateInterval = [Date | null, Date | null]

export interface InvoiceFile {
    afsInvoiceId: number
    invoiceFileName: string
    invoiceDate: string
}

export interface PageParams {
    pageNumber: number
    pageSize: number
    search: string
    sortBy: string
    sortDirection: "DESC" | "ASC"
}

export interface PageRequest {
    page: Partial<PageParams>
}

export type PaymentMethod = 'BANK_TRANSFER' | 'CRYPTO_TRC20' | 'NETELLER' | 'SKRILL'

export interface PaymentMethodDetails {
    id: number
    status: "ACTIVE" | "INACTIVE" | "PENDING"
    paymentMethod: PaymentMethod
}

export interface ManagerContact {
    email: string
    name: string
    phoneNumber: string
}

export interface NCIItem {
    id: number
    nciId: number
    imageName: string
    imageUrl: string
    originalImageName: string
    html: string
    type: string
    language: string
    name: string
    size: string
    landingPage: string
    createdDate: string
}


export type MainTier = 'Gold' |
    'Bronze' |
    'Silver' |
    'Platinum' |
    'ActiveTrader' |
    'GoldTH' |
    'PlatinumTT' |
    'Diamond' |
    'Influencer'

export type Tier = MainTier | string & {}

export interface PartnerInfo {
    id: number
    path: string
    commissionTradingAccountId: number
    createdDate: string
    userId: number
    isDisabled?: boolean
    retailTrackingLink?: string
    partnerTrackingLink?: string
    retailTrackingLinkThinkVina?: string
    partnerTrackingLinkThinkVina?: string
    hasSubIbsPermission?: boolean
    tier: Tier
    calculationModel: 'Standard' | 'Vietnam'
    partnerType: 'IB'
    isClientPaymentsDisplayed?: boolean
}

export type GetPartnerInfoResponse = PartnerInfo[]

export interface ReportBasicFilters {
    userId: number
    userEmail: string | null
    clientCountries: number[] | null
    accountNumber: number | null
    clientPlatformTypes: number[] | null
    affiliateAssociationCreatedStartDate: string | null
    affiliateAssociationCreatedEndDate: string | null
    // commissionGeneratedStartDate: string | null
    // commissionGeneratedEndDate: string | null
}

export type SearchPartnerUserSummaryParams = ReportBasicFilters
export type SearchPartnerClientListParams = ReportBasicFilters & {
    offset?: number
    limit?: number
}

export interface PartnerUserSummary {
    partnerId: number
    // retailTradingAccountsNumber: number
    retailUserAccountsNumber: number
    lotsSum: number
    amountSum: number
}

export type PartnerUserSummaryResponse = PartnerUserSummary[]

export interface IBCountry {
    id: number
    name: string
}

export type IBCountryResponse = IBCountry[]

export type IBTradingPlatformName = "MT4" | "MT5" | "cTrader" | "ThinkTrader_Legacy" | "ThinkTrader"

export interface IBTradingPlatform {
    id: number
    name: IBTradingPlatformName
    supportsNetting: boolean
}

export type IBTradingPlatformResponse = IBTradingPlatform[]

export interface AccountTradingType {
    id: number
    accountTradingType: "CFD" | "CE"
    calculateMargin: boolean
    description: string
    accountLimit: number
    initialAppRequired: boolean
    countryRestricted: boolean
    applicationAge: number
    allowNegativeCashBalance: boolean
    countryIdList?: string
}

export type AccountTradingTypeResponse = AccountTradingType[]

export type CashbackType = "PERCENTAGE" | "VALUE"

export interface AccountsSummary {
    accounts: number
    transactions: number
    lotsSum: number
    amountSum: number
    activeTraders: number | null
    notionalValueSum: number | null
    netFundingSum: number | null
}

export type ClientSummaryResponse = AccountsSummary[]

export type SearchClientTransactionsResponse = ClientTransaction[]

export interface CashBackTypeObject {
    id: number
    name: CashbackType
    createdDate: string
}

export interface UpdateCashbackParams {
    userTradingAccountId: number
    partnerId: number
    cashbackTypeId: number
    cashbackValue: number
}

export interface ClientTransaction {
    accountId: number
    accountNumber: number
    tradingPlatform: IBTradingPlatformName
    accountTradingType: "CFD" | "CE"
    accountType: "LIVE" | "DEMO"
    platformAccountType: string
    symbol?: string
    lots: number
    amount: number
}

export interface PartnerClient {
    partnerId: number
    clientId: number
    clientName: string
    country: string
    associationDate: string
}

export interface PartnerClientListResponse {
    items: PartnerClient[]
    totalCount: number
    offset: number
    limit: number
}

export interface ClientBasicFiltersParams {
    userId: number
    partnerId: number
    clientPlatformTypes: number[] | null
    affiliateAssociationCreatedStartDate: string | null
    affiliateAssociationCreatedEndDate: string | null
    commissionGeneratedStartDate: string | null
    commissionGeneratedEndDate: string | null

}

export type SearchClientTransactionsParams = ClientBasicFiltersParams

export type SearchClientSummaryParams = ClientBasicFiltersParams

export type SearchClientAllAccountsParams = ClientBasicFiltersParams

export interface TradingAccount {
    accountId: number
    accountNumber: number
    tradingPlatform: IBTradingPlatformName
    platformAccountType: string
    transactions: number
    hasCashback: boolean
    lotsSum: number
    amountSum: number
    cashbackType?: CashbackType
    cashbackValue?: number
    clientName?: string
}

export type SearchClientAllAccountsResponse = TradingAccount[]

export interface GetWithUserIDParams {
    userId: number
}

export interface AvailableInvoice {
    id: number
    amount: number
    isPaid: boolean
    hasToBePaid: boolean
    fromDate: string
    toDate: string
    createdDate: string
}

export interface RequestInvoicesPaymentParams {
    invoiceIds: number[]
}

export interface PayoutHistory {
    id: number
    createdDate: string
    status?: "APPLIED" | 'string'
}

export type IBApplicationStatus = ApplicationStatus
export type IBCustomApplicationStatuses = "Pending review" | "In progress" | "Unknown" | "Approved" | "Denied"

export type IBApplicationStep = "personal info" |
    "phone" |
    "questionnaire" |
    "address data" |
    "address kyc" |
    "id kyc"

export interface UserPartnerApplication {
    accountType: "Commission"
    firstName: string
    lastName: string
    email: string
    submitDate: string
    status: ApplicationStatus
    organizationName: PortalAccountDomain
}

export type IBApplicationInit = {
    accountType: "Commission"
    organizationId: number
    countryId: number
    status: string
    rawApplication: string
    formStep: IBApplicationStep
    currencyId: number
    platform: "MT4"
    platformAccountType: "Commission"
    partnerType: "IB" //or Affiliate
    afsAid?: string
    preferredLanguage: number
    accountHolderDayOfBirth?: number
    accountHolderMonthOfBirth?: number
    accountHolderYearOfBirth?: number
}


export type IBApplication = {
    applicationId: number
    accountType: string
    countryName: string
    firstName: string
    lastName: string
    email: string
    submitDate: Date
    status: string
    organizationName: string
    currencyCode: string
    ipAddress: string
    platformName: string
    formStep: IBApplicationStep
    platformAccountType: string
    leverage: number
}
export interface IBAddress {
    applicationId: number
    accountHolderSubBuilding: string
    accountHolderBuilding: string
    accountHolderStreet: string
    accountHolderCity: string
    accountHolderRegion: string
    accountHolderStreetAddress: string
    accountHolderStreetAddress2: string
    accountHolderStateProvince: string
    accountHolderPostalCode: string
    accountHolderFloor: string
    accountHolderSubCity: string
    accountHolderPremise: string
}

export interface IBAccountCreatedResponse {
    "application id": number
    "user id": number
    accountNumber: number
}

export interface IBContactPhone {
    applicationId: number
    accountHolderPhone: string
    accountHolderPhoneCode: number
    accountHolderMobilePhone: string
    accountHolderMobilePhoneCode: number
}
export interface DefaultPartnerResponse {
    "Partner Id": number
    "Payment Cycle": string
    "Tier": string
    "Path": string
    "Account Number №": number
    "applicationId": number
}

export type SearchTradingAccountListBasicFilters = {
    partnerId: number
    userEmail: string | null
    clientCountries: number[] | null
    clientPlatformTypes: number[] | null
    affiliateAssociationCreatedStartDate: string | null
    affiliateAssociationCreatedEndDate: string | null
    commissionGeneratedStartDate: string | null
    commissionGeneratedEndDate: string | null
}

// Active "My Portfolio" report, sent to the summary endpoint so the back end
// computes the tiles over the same population the report list returns (TMJV-955/956).
// Case-insensitive on the BE; an absent/unknown value keeps the legacy whole-book
// behaviour.
export type MyPortfolioReportType =
    | 'ACCOUNTS'
    | 'UNFUNDED'
    | 'PENDING'
    | 'LEADS'
    | 'REBATES'
    | 'NET_FUNDING'

// The summary action accepts trading-account inputs the plain list endpoint does
// not, so its param type extends the shared base. reportType (TMJV-955/956) lets
// the BE summarise per active report; invoiceStatus (rebates) is now expressed by
// the summary query (TMJV-955), so it flows through too.
export type SearchPartnerTradingAccountsSummaryParams = SearchTradingAccountListBasicFilters & {
    reportType?: MyPortfolioReportType
    tradingAccountNumber?: number | null
    tradingAccountId?: number | null
    applicationStatus?: string | null
    invoiceStatus?: string | null
}
export type SearchPartnerAllTradingAccountsParams = SearchTradingAccountListBasicFilters

export interface TradeTransaction {
    tradeDate: string
    ticket: string
    accountNumber: string
    platformName: string
    platformAccountType: string
    symbol: string
    commissionAmount: number
    commissionGeneratedDate: string
    lots: number
}

export type GetTraderTransactionReportBasicFilter = {
    partnerId: number
    email: string | null
    accountNumber: string | null
    countryIds: number[] | null
    tradingPlatformIds: number[] | null
    symbol: string | null
    associationDateFrom: string | null
    associationDateTo: string | null
    commissionDateFrom: string | null
    commissionDateTo: string | null
}

export type GetTraderTransactionReportParams = GetTraderTransactionReportBasicFilter & {
    offset?: number
    limit?: number
    fetchAll?: boolean
}

export interface TraderTransactionReportResponse {
    items: TradeTransaction[]
    totalCount: number
    offset: number
    limit: number
}

export interface PartnerCountrySettings {
    id: number
    countryId: number
    countryName: string
    partnershipTypeId: number
    partnershipTypeName: string
    isActive: boolean
}

export interface SubLevelIB {
    subIbId: number
    name: string
    countryName: string
    tier: string
    level: number
    path?: string
    subIbEarning: number
    commission: number
    isVietnam: boolean
    traderLots: number
    subIbEarningSummary: number
    commissionSummary: number
    sublevelPartners: number
}

export interface SubLevelIBSummary {
    sublevelPartners: number
    subIbEarnings: number
    commissions: number
}

export interface VTsubibsExportItem {
    partnerId: number
    tier: string
    path: string
    level: number
    createdDate: string
    updatedDate: string
    symbol: string
    clientAccountId: string
    closedTime: string
    lots: number
    clientNominalVolumeUsd: number
    userTradingAccountId: number
    dataMartPositionStatId: string
}

export type SearchSubIBsListBasicFilters = {
    partnerId: number
    clientCountries: number[] | null
    subIbId: number | null
    userEmail: string | null
    commissionGeneratedStartDate?: string | null
    commissionGeneratedEndDate?: string | null
}

export type GetSubLevelSummaryParams = SearchSubIBsListBasicFilters
export type GetSubLevelIbsParams = SearchSubIBsListBasicFilters

export type GetMyPortfolioAccountReportBasicFilter = {
    partnerId: number,
    associationStartDate: string | null,
    associationEndDate: string | null,
    commissionStartDate: string | null,
    commissionEndDate: string | null,
    country: number | null,
    email: string | null,
    platformType: number | null,
    tradingAccountNumber: number | null
}

export type GetMyPortfolioAccountReportParams = GetMyPortfolioAccountReportBasicFilter

export type MyPortfolioAccountReport = {
    clientName: string,
    account: string,
    email: string,
    phoneNumber?: string,
    platformType: string,
    accountType: string,
    balance: number,
    deposits: number,
    volume: number,
    eodBalance?: number | null,
    eodEquity?: number | null,
    eodCurrency?: string | null,
    eodSnapshotDate?: string | null,
    lastTradeDate?: string | null
}

export type GetMyPortfolioAccountReportResponse = MyPortfolioAccountReport[]

export type GetMyPortfolioUnfundedAccountsReportBasicFilter = {
    partnerId: number,
    country: number | null,
    email: string | null,
    tradingAccountNumber: number | null,
    platformType: number | null,
    associationStartDate: string | null,
    associationEndDate: string | null
}

export type GetMyPortfolioUnfundedAccountsReportParams = GetMyPortfolioUnfundedAccountsReportBasicFilter

export type MyPortfolioUnfundedAccountReport = {
    account: string,
    email: string,
    platformType: string,
    accountType: string
}

export type GetMyPortfolioUnfundedAccountsReportResponse = MyPortfolioUnfundedAccountReport[]


export type GetMyPortfolioPendingReportBasicFilter = {
    partnerId: number,
    country: number | null,
    email: string | null,
    associationStartDate: string | null,
    associationEndDate: string | null,
    applicationStatus: string | null
}

export type GetMyPortfolioPendingReportParams = GetMyPortfolioPendingReportBasicFilter

export type MyPortfolioPendingReport = {
    userId: number,
    clientName: string,
    email: string,
    applicationStatus: string
}

export type GetMyPortfolioPendingReportResponse = MyPortfolioPendingReport[]

export type GetMyPortfolioLeadsReportBasicFilter = {
    partnerId: number,
    countryId: number | null,
    email: string | null
}

export type GetMyPortfolioLeadsReportParams = GetMyPortfolioLeadsReportBasicFilter

export type MyPortfolioLeadsReport = {
    demoTradingAccount: string,
    clientName: string,
    email: string
}

export type GetMyPortfolioLeadsReportResponse = MyPortfolioLeadsReport[]

export type GetMyPortfolioRebatesReportBasicFilter = {
    partnerId: number,
    email: string | null,
    tradingAccountId: number | null,
    invoiceStatus: string | null,
    from: string | null,
    to: string | null
}

export type GetMyPortfolioRebatesReportParams = GetMyPortfolioRebatesReportBasicFilter

export type MyPortfolioRebatesReport = {
    tradingAccount: string,
    email: string,
    invoiceStatus: string,
    commissionDate: string
}

export type GetMyPortfolioRebatesReportResponse = MyPortfolioRebatesReport[]

export type GetMyPortfolioNetFundingReportBasicFilter = {
    partnerId: number,
    countryId: number | null,
    email: string | null,
    tradingAccountId: number | null,
    platformTypeId: number | null,
    commissionFrom: string | null,
    commissionTo: string | null,
    associationFrom: string | null,
    associationTo: string | null
}

export type GetMyPortfolioNetFundingReportParams = GetMyPortfolioNetFundingReportBasicFilter

export type MyPortfolioNetFundingReport = {
    tradingAccount: string,
    clientName: string,
    email: string,
    platformType: string,
    accountType: string,
    volume: number,
    netFunding: number,
    commissionGenerated: number,
    cashbackGenerated: number
}

export type GetMyPortfolioNetFundingReportResponse = MyPortfolioNetFundingReport[]

export type GetMyPortfolioClientPaymentsReportBasicFilter = {
    partnerId: number,
    fromDate?: string | null,
    toDate?: string | null,
    email?: string | null,
    status?: string | null
}

export type GetMyPortfolioClientPaymentsReportParams = GetMyPortfolioClientPaymentsReportBasicFilter

export type MyPortfolioClientPaymentsReport = {
    firstName: string,
    lastName: string,
    email: string,
    dateSubmitted: string,
    type: string,
    paymentMethod: string,
    amount: number,
    currency: string,
    status: string,
    lastDepositDate: string | null,
    lastWithdrawalDate: string | null
}

export type GetMyPortfolioClientPaymentsReportResponse = MyPortfolioClientPaymentsReport[]


export type AddPartnerReportNotesParams = {
    userId: number,
    dmClientTradingAccountId: string,
    note: string
}

export type GetPartnerReportNotesParams = {
    dmClientTradingAccountId: string,
    userId: string
}

export type GetPartnerReportNotesResponse = {
    dmClientTradingAccountId: string,
    userFirstName: string,
    userLastName: string,
    note: string,
    createdDate: Date
}


export type AssetClassSymbols = {
    id: number,
    assetClass: string,
    symbol: string
}

export type MasterIBSummaryParams = {
    partnerId: number,
    subIbPartnerId?: number | null,
    tradeSymbol?: string,
    commissionStartDate?: string | null,
    commissionEndDate?: string | null,
    associationStartDate?: string | null,
    associationEndDate?: string | null,
}

export type MasterIBSummaryRequest = MasterIBSummaryParams

export type MasterIBSummary = {
    totalSubIbs: number,
    totalCommissionsAllSubIbs: number,
    totalNetCommissionsMasterIb: number,
    totalVolumeLots: number,
    totalVolumeUsd: number,
    totalNetFunding: number
}

export type MasterIBSummaryResponse = MasterIBSummary

export type MasterIBHierarchyReportParams = {
    partnerId: number,
    subIbPartnerId?: number | null,
    tradeSymbol?: string,
    commissionStartDate?: string | null,
    commissionEndDate?: string | null,
    associationStartDate?: string | null,
    associationEndDate?: string | null,
    offset?: number,
    limit?: number,
}

export type MasterIBHierarchyReportRequest = MasterIBHierarchyReportParams

interface InstrumentBreakdowns {
    instrumentType: string,
    volume: number,
    notionalValue: number,
    commissionsForSubIb: number,
    netCommissionsForMasterIb: number
}

export interface MasterIBHierarchy {
    subIbPartnerId: number,
    parentPartnerId: number,
    subIbName: string,
    hierarchyLevel: number,
    tierName: string,
    netFunding: number,
    volume: number,
    notionalValue: number,
    commissionsForSubIb: number,
    netCommissionsForMasterIb: number,
    instrumentBreakdowns: InstrumentBreakdowns[],
}

export type MasterIBHierarchyReport = {
    items: MasterIBHierarchy[],
    totalCount: number,
    offset: number,
    limit: number
}

export type MasterIBHierarchyReportResponse = MasterIBHierarchyReport

export type MasterIBHierarchyReportExcelDataParams = {
    partnerId: number,
    subIbPartnerId?: number | null,
    tradeSymbol?: string,
    commissionStartDate?: string | null,
    commissionEndDate?: string | null,
    associationStartDate?: string | null,
    associationEndDate?: string | null,
    includeTransaction?: boolean
}

export type MasterIBHierarchyReportExcelDataRequest = MasterIBHierarchyReportExcelDataParams

export interface HierarchyReport {
    subIbPartnerId: number,
    parentPartnerId: number,
    subIbName: string,
    hierarchyLevel: number,
    tierName: string,
    netFunding: number,
    volume: number,
    notionalValue: number,
    commissionsForSubIb: number,
    netCommissionsForMasterIb: number,
    instrumentBreakdowns: InstrumentBreakdowns[],
}

export interface Transactions {
    commissionsForSubIb: number,
    instrument: string,
    instrumentType: string,
    netCommissionsForMasterIb: number,
    subIbPartnerId: number,
    transactionDate: string,
    transactionId: number,
    volumeLots: number,
    volumeUsd: number,

}

export type MasterIBHierarchyReportExcelData = {
    hierarchyReport: HierarchyReport[],
}

export type MasterIBHierarchyReportExcelDataIncludeTransaction = {
    transactions: Transactions[],
}

export type MasterIBHierarchyReportExcelDataResponse = MasterIBHierarchyReportExcelData